import React, { Component, RefObject } from "react";
import { connect } from "react-redux";
import { Redirect } from "react-router";
import { saveNewCategoryFilterSelection } from "../../../redux/actions/categoryAction";
import { toggleHalfThePriceProducts } from "../../../redux/actions/itemActions";
import { hideModal, showModal } from "../../../redux/actions/modalActions";
import { saveNewSearch } from "../../../redux/actions/searchActions";
import { resetCss } from "../../../utils/modal";
import { searchHasValue } from "../../../utils/searchFunctions";
import variables from "../../../styles/utils/variables.scss";
import { Icon } from "../../icons/Icon";
// Removed unused import: SearchSuggestionsPopup
import EnhancedSearchSuggestionsPopup from "../../popups/EnhancedSearchSuggestionsPopup";
import { fetchSearchSuggestions } from "../../../clients/shopify/shopifyClient";

interface Props {
  search: SearchState;
  categories: CategoryState;
  isHalfThePriceClicked?: boolean;
  saveNewSearch: any;
  saveNewCategoryFilterSelection: any;
  searchInput: RefObject<HTMLInputElement>;
  toggleHalfThePriceProducts: (
    isHalfThePriceClicked: boolean,
    callTheAPI: boolean
  ) => void;
  showModal: any;
  hideModal: any;
}

interface State {
  userHasSearched: boolean;
  items: { id: string; name: string }[];
  topMargin: number;
  bannerHeight: number;
}

class SearchProducts extends Component<Props, State> {
  state = {
    userHasSearched: false,
    items: [],
    topMargin: 242,
    bannerHeight: 0,
  };

  suggestionsRef: any = React.createRef();

  componentDidMount = () => {
    document.addEventListener("click", this.handleClickOutside, true);
    window.addEventListener("scroll", this.onScroll);
  };

  componentWillUnmount() {
    document.removeEventListener("click", this.handleClickOutside, true);
    window.removeEventListener("scroll", this.onScroll);
  };

  componentDidUpdate = (prevProps: Props) => {
    // if user searches, reset to display component
    if (this.state.userHasSearched) {
      this.setState({ userHasSearched: false });
    }

    if (
      (prevProps.categories.selectedNode !==
        this.props.categories.selectedNode &&
        this.props.searchInput.current) ||
      (this.props.isHalfThePriceClicked !== prevProps.isHalfThePriceClicked &&
        this.props.searchInput.current)
    ) {
      this.props.searchInput.current.value = "";
    }

    if (
      prevProps.search.searchTerm !== this.props.search.searchTerm &&
      this.props.searchInput.current
    ) {
      this.props.searchInput.current.value = this.props.search.searchTerm;
    }
  };

  onKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const characterCode = e.charCode || e.keyCode || e.key;
    // if user presses enter
    if (characterCode === 13) {
      this.submitSearchTerm();
      document.body.style.overflow = "auto";
    }

    // if user presses escape
    if (characterCode === 27) {
      this.clearSuggestions();
    }
  };

  onScroll = () => {
    this.handleOnScroll();
  };

  submitSearchTerm = async (query?: string) => {
    let value = query ? query : this.props.searchInput?.current?.value;

    // Use the enhanced ranking algorithm for full searches when pressing Enter or clicking "View all results"
    // This ensures better results for searches like "eggs" showing actual egg products first
    await this.props.saveNewSearch(value, true);

    await this.props.toggleHalfThePriceProducts(false, false);

    if (!searchHasValue(this.props.search)) {
      await this.props.saveNewCategoryFilterSelection(null);
    }

    this.setState({ userHasSearched: true });

    this.clearSuggestions();
  };

  handleOnSearch = async (e: any) => {
    const characterCode = e.charCode || e.keyCode || e.key;
    // if user presses escape
    if (characterCode === 27) {
      return this.clearSuggestions();
    }
    // if user presses input except enter
    if (characterCode !== 13) {
      const query = this.props.searchInput?.current?.value;

      if (query?.length === 0) return;

      this.searchApi(query);

      this.handleOnScroll();
    }
  };

  handleOnScroll = () => {
    const pixelsScrolled =
      document.body.scrollTop || document.documentElement.scrollTop;

    const headerHeight =
      document.getElementById("search-navbar")?.getBoundingClientRect()
        .height || 0;

    const bannerHeight =
      document.getElementById("banner")?.getBoundingClientRect().height || 0;

    if (pixelsScrolled > headerHeight) {
      this.setState({
        topMargin: 242 - headerHeight,
      });
    } else {
      this.setState({ topMargin: 242 + bannerHeight - pixelsScrolled });
    }
  };

  handleClickOutside = (e: MouseEvent) => {
    // Get the search input element
    const searchInput = this.props.searchInput.current;
    const searchContainer = searchInput?.closest('.u-flex');
    
    // Get the modal container
    const modalContainer = document.querySelector('.o-modal');
    
    // If we have a modal open and the click is outside both the search input and modal
    if (modalContainer) {
      const isClickOnSearchInput = searchContainer && searchContainer.contains(e.target as Node);
      const isClickOnModal = modalContainer.contains(e.target as Node);
      
      // If clicked outside both the search input and modal, close the suggestions
      if (!isClickOnSearchInput && !isClickOnModal) {
        this.clearSuggestions();
      }
    }
  };

  clearSuggestions = () => {
    this.props.hideModal();
    document.body.style.overflow = "auto";
    this.setState({ items: [] });
    resetCss();
  };

  debounce = (func: (query: string) => {}, timeout = 500) => {
    let timer: any;

    return (...args: any) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        func.apply(this, args);
      }, timeout);
    };
  };

  searchApi = this.debounce(async (query: string) => {
    try {
      // Use Shopify's predictive search API instead of the local backend
      const suggestions = await fetchSearchSuggestions(query);
      
      if (suggestions.products.length > 0 || suggestions.collections.length > 0 || suggestions.queries.length > 0) {
        // Format suggestions for display
        const formattedSuggestions = [
          ...suggestions.products.map(product => ({
            id: product.id,
            name: product.title,
            type: 'product',
            url: `/browse?s=${encodeURIComponent(product.title)}`,
            image: product.image || ''
          })),
          ...suggestions.collections.map(collection => ({
            id: collection.id,
            name: collection.title,
            type: 'collection',
            url: `/browse/${collection.handle}`
          })),
          ...suggestions.queries.map(query => ({
            id: `query-${query}`,
            name: query,
            type: 'query',
            url: `/browse?s=${encodeURIComponent(query)}`
          }))
        ];
        
        this.setState({ items: formattedSuggestions });
        this.openSuggestionModal();
      }
    } catch (error) {
      console.error('Error fetching search suggestions:', error);
    }
  });

  openSuggestionModal = () => {
    // Get the current search query
    const query = this.props.searchInput?.current?.value || '';
    
    const modalSearchSuggestions = () => (
      <EnhancedSearchSuggestionsPopup 
        items={this.state.items} 
        query={query}
      />
    );

    const top = this.state.topMargin + this.state.bannerHeight;
    // Get the search input element to match its width and position
    const searchInput = this.props.searchInput.current;
    if (searchInput) {
      const rect = searchInput.getBoundingClientRect();
      
      // Find the parent container that includes the search icon
      const searchContainer = searchInput.closest('.u-flex');
      let containerRect = rect;
      
      if (searchContainer) {
        containerRect = searchContainer.getBoundingClientRect();
      }
      
      this.props.showModal({
        component: modalSearchSuggestions,
        style: {
          position: 'absolute',
          top: top + "px",
          left: containerRect.left + "px",
          width: containerRect.width + "px",
          maxWidth: containerRect.width + "px"
        },
        className: 'u-no-background', // Add a class to hide the background
        isSearchSuggestion: true // Flag to indicate this is a search suggestion
      });
    } else {
      this.props.showModal({
        component: modalSearchSuggestions,
        style: {
          top: top + "px",
        },
        className: 'u-no-background', // Add a class to hide the background
        isSearchSuggestion: true // Flag to indicate this is a search suggestion
      });
    }

    document.body.style.overflow = "hidden";
  };

  render() {
    let fill = variables.violetColor;

    if (window.innerWidth <= 600) {
      fill = "#fff";
    }
    if (this.state.userHasSearched) {
      if (searchHasValue(this.props.search)) {
        return <Redirect to={"/browse?s=" + this.props.search.searchTerm} />;
      }
      return <Redirect to="/browse" />;
    }
    return (
      <div className="u-w-all">
        <div className="u-flex u-align-center u-h-all">
          <button onClick={() => this.submitSearchTerm()}>
            <Icon id="search" size="l" fill={fill} />
          </button>
          <input
            defaultValue={this.props.search.searchTerm}
            type="text"
            placeholder="Search for products"
            className="u-main-font--vsmall u-w-all u-semi-bold"
            style={{ paddingLeft: "13px", border: "none", fontSize: "14px" }} // to align with the icon
            ref={this.props.searchInput}
            onKeyUp={(e) => this.handleOnSearch(e)}
            onKeyDown={(e) => this.onKeyPress(e)}
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: ReduxState) => ({
  search: state.search,
  categories: state.categories,
  isHalfThePriceClicked: state.items.isHalfThePriceClicked,
  searchInput: React.createRef<HTMLInputElement>(),
});

export default connect(mapStateToProps, {
  saveNewSearch,
  saveNewCategoryFilterSelection,
  toggleHalfThePriceProducts,
  showModal,
  hideModal,
})(SearchProducts);
